import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Fuel, Turtle, Gauge, Rocket } from "lucide-react";

export default function GasTracker() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    api.get("/tools/gas-tracker")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, []);

  const Card = ({ icon: Icon, label, gwei, usd, highlight }) => (
    <div className={"bg-[var(--bg-panel)] border p-6 text-center overflow-hidden " + (highlight ? "border-yellow-400/40" : "border-[var(--border-soft)]")}>
      <Icon className={"w-6 h-6 mx-auto mb-3 " + (highlight ? "text-yellow-400" : "text-[var(--text-tertiary)]")} />
      <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">{label}</div>
      <div className="font-display font-bold text-3xl text-[var(--text-primary)] break-all">{Number(gwei).toFixed(3)}</div>
      <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1">gwei</div>
      {usd !== null && usd !== undefined && (
        <div className="text-xs font-mono text-[var(--text-tertiary)] mt-3 pt-3 border-t border-[var(--border-soft)] break-all">
          ~${usd.toFixed(4)} per transfer
        </div>
      )}
    </div>
  );

  return (
    <div className="pt-28 pb-20 max-w-2xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <Fuel className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">ETH Gas Tracker</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          Live Ethereum network gas prices, updated every 20 seconds.
        </p>
      </div>

      {error ? (
        <p className="text-center text-red-400 font-mono text-sm">Could not load gas data. Try again shortly.</p>
      ) : !data ? (
        <p className="text-center text-[var(--text-tertiary)] font-mono text-sm">Loading current gas prices...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card icon={Turtle} label="Safe" gwei={data.safe} usd={data.transfer_cost_usd.safe} />
            <Card icon={Gauge} label="Standard" gwei={data.propose} usd={data.transfer_cost_usd.propose} highlight />
            <Card icon={Rocket} label="Fast" gwei={data.fast} usd={data.transfer_cost_usd.fast} />
          </div>
          <p className="text-center text-xs font-mono text-[var(--text-tertiary)] mt-4">
            Base fee: {data.base_fee} gwei · ETH price: ${data.eth_price?.toLocaleString()}
          </p>
        </>
      )}

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How the Gas Tracker works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Read the network</div>
            <p className="text-xs text-[var(--text-secondary)]">Ethereum's current gas oracle is checked, reflecting real transaction fee demand right now.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. Three speed tiers</div>
            <p className="text-xs text-[var(--text-secondary)]">Safe confirms slower but costs less, Fast confirms quickly for a premium, Standard is the balanced middle ground.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Convert to dollars</div>
            <p className="text-xs text-[var(--text-secondary)]">Each gwei price is converted into an estimated USD cost for a standard ETH transfer, using the live ETH price.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          Gas is the fee paid to Ethereum validators to process a transaction, priced in gwei, a fraction of ETH. Prices rise when the network is busy and fall during quiet periods, so timing a transaction for a low-gas window can meaningfully reduce costs, especially for larger or more complex transactions like token swaps.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          The dollar estimate here assumes a simple 21,000 gas transfer, the cost of sending plain ETH. Token transfers, swaps, and smart contract interactions use significantly more gas and will cost more than the figures shown. This tool is informational only and is not financial advice.
        </p>
      </div>
    </div>
  );
}